import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

const { FiLock, FiEye, FiEyeOff } = FiIcons;

const ResetPasswordForm = () => {
    const { resetPassword, signOut, user, isPasswordRecovery, loading: authLoading, isProcessingTokens } = useAuth();
    const navigate = useNavigate();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');

    useEffect(() => {
        console.log('🔄 ResetPasswordForm mounted/updated', {
            hasUser: !!user,
            isPasswordRecovery,
            userId: user?.id,
            authLoading,
            isProcessingTokens
        });
        
        // Wait for both auth loading and token processing to complete before making decisions
        const isStillInitializing = authLoading || isProcessingTokens;
        
        // Check if there are tokens in the URL that might be processed soon
        const hasTokensInUrl = window.location.hash && window.location.hash.includes('access_token');

        if (!isStillInitializing && (!user || !isPasswordRecovery)) {
            if (hasTokensInUrl) {
                console.log('⏳ Tokens detected in URL, waiting for processing...');
                return;
            }
            console.log('❌ User not authenticated for password reset after initialization, redirecting to auth');
            navigate('/auth');
        }
    }, [user, isPasswordRecovery, authLoading, isProcessingTokens, navigate]);

    // Show loading while auth is initializing or processing tokens
    if (authLoading || isProcessingTokens) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center mt-4 pt-safe">
                <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md mx-auto text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <h2 className="text-xl font-semibold text-gray-900 mb-2">
                        {isProcessingTokens ? 'Processing Reset Link...' : 'Loading...'}
                    </h2>
                    <p className="text-gray-600">
                        {isProcessingTokens ? 'Validating your password reset request' : 'Please wait'}
                    </p>
                </div>
            </div>
        );
    }

    // Show error if not in password recovery mode
    if (!user || !isPasswordRecovery) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center mt-4 pt-safe">
                <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md mx-auto">
                    <div className="text-center">
                        <h2 className="text-2xl font-bold text-gray-900 mb-4">Invalid Reset Link</h2>
                        <p className="text-gray-600 mb-6">This password reset link is invalid or has expired.</p>
                        <button
                            onClick={() => navigate('/forgot-password')}
                            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                        >
                            Request New Reset Link
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (password !== confirmPassword) {
            setError("Passwords do not match.");
            toast.error("Passwords do not match.");
            return;
        }

        setLoading(true);
        setError('');
        setMessage('');

        try {
            console.log('🔐 Attempting to reset password...');
            const { error: updateError } = await resetPassword(password);

            if (updateError) {
                console.log('❌ Password reset failed:', updateError.message);
                throw updateError;
            }

            console.log('✅ Password reset successful');
            setMessage("Password updated successfully! You are now logged in.");
            toast.success("Password updated successfully!");

            setTimeout(() => {
                console.log('🔄 Navigating to dashboard...');
                navigate('/dashboard');
            }, 2000);

        } catch (err) {
            console.log('❌ Password reset error:', err.message);
            setError(err.message);
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = async () => {
        try {
            console.log('🚪 User canceled password reset, signing out recovery session...');
            await signOut();
            navigate('/auth');
        } catch (error) {
            console.error('Error during cancel:', error);
            // Navigate anyway
            navigate('/auth');
        }
    };

    return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4 mt-4 pt-safe">
            <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full max-w-md mx-auto"
            >
                <div className="bg-white rounded-2xl shadow-xl p-8">
                    <div className="text-center mb-8">
                        <h2 className="text-3xl font-bold text-gray-900 mb-2">Reset Password</h2>
                        <p className="text-gray-600">Enter your new password below</p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            New Password
                        </label>
                        <div className="relative">
                            <SafeIcon
                                icon={FiLock}
                                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                            />
                            <input
                                type={showPassword ? 'text' : 'password'}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="Enter your new password"
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                <SafeIcon icon={showPassword ? FiEyeOff : FiEye} />
                            </button>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Confirm New Password
                        </label>
                        <div className="relative">
                            <SafeIcon
                                icon={FiLock}
                                className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                            />
                            <input
                                type={showConfirmPassword ? 'text' : 'password'}
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                                className="w-full pl-10 pr-12 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                placeholder="Confirm your new password"
                            />
                            <button
                                type="button"
                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                            >
                                <SafeIcon icon={showConfirmPassword ? FiEyeOff : FiEye} />
                            </button>
                        </div>
                    </div>

                    {error && (
                        <div className="text-red-500 text-sm">{error}</div>
                    )}

                    <div className="space-y-3">
                        <motion.button
                            type="submit"
                            disabled={loading}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className="w-full bg-blue-600 text-white py-3 rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {loading ? 'Resetting...' : 'Reset Password'}
                        </motion.button>
                        
                        <motion.button
                            type="button"
                            onClick={handleCancel}
                            disabled={loading}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            className="w-full bg-gray-200 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            Cancel & Return to Login
                        </motion.button>
                    </div>
                </form>

                    {message && (
                        <div className="mt-4 text-center text-green-600">
                            {message}
                        </div>
                    )}
                </div>
            </motion.div>
        </div>
    );
};

export default ResetPasswordForm;